import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConsentLayout } from './ConsentLayout';
import {
  getConsentDraft,
  newInviteToken,
  setConsentDraft,
} from '../../../lib/consent/consentDraft';
import { sendEmail } from '@4cm/core/email';

/**
 * Step 2 — child enters their parent's email.
 *
 * On submit: stores the email + a fresh invite token in the draft,
 * fires the parentInvite Resend template (existing edge function), then
 * routes to Step 3 (pending invite).
 *
 * Existing parentInvite template requires inviter_name + team_name;
 * for self-led signup we pass placeholders. A dedicated template for
 * self-signup may land in a follow-up — copy is the only reason to
 * change it; the wiring works.
 */
export function ParentEmail() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guard: if Step 1 hasn't been completed, send back.
  useEffect(() => {
    const draft = getConsentDraft();
    if (!draft?.childDob) {
      navigate('/parent/consent/dob', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("That doesn't look like a valid email.");
      return;
    }
    const draft = getConsentDraft();
    if (!draft?.childDob) {
      setError('Your date of birth was lost. Please start over.');
      navigate('/parent/consent/dob');
      return;
    }
    const token = draft.inviteToken ?? newInviteToken();
    setConsentDraft({ parentEmail: email, inviteToken: token });

    setSubmitting(true);
    const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '';
    const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '';

    const acceptUrl =
      `${window.location.origin}/parent/consent/review` +
      `?token=${encodeURIComponent(token)}` +
      `&dob=${encodeURIComponent(draft.childDob)}`;

    const result = await sendEmail({
      supabaseUrl,
      anonKey,
      template: 'parentInvite',
      to: email,
      vars: {
        parent_name: 'Parent or Guardian',
        child_name: 'your child',
        inviter_name: '4CM (parent-led signup)',
        team_name: '4CM',
        accept_url: acceptUrl,
      },
    });
    setSubmitting(false);

    if (!result.ok) {
      setError(`Could not send the invite email: ${result.error ?? 'unknown error'}.`);
      return;
    }

    navigate('/parent/consent/pending');
  };

  return (
    <ConsentLayout
      step={2}
      title="Tell us your parent's email"
      lead="We'll send your parent a link. They have to say yes before you can use 4CM. We don't store anything else from you yet."
    >
      <form className="consent-form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <div className="consent-form__field">
          <label className="consent-form__label" htmlFor="parent-email">Parent's email</label>
          <input
            id="parent-email"
            type="email"
            className={`consent-form__input ${error ? 'consent-form__input--error' : ''}`}
            placeholder="parent@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            required
          />
          <span className="consent-form__sub">
            They'll get an email from 4CM with a link to the consent form. The link expires in 7 days.
          </span>
          {error && <span className="consent-form__error">{error}</span>}
        </div>

        <div className="consent-form__actions">
          <button type="submit" className="consent-form__btn" disabled={submitting}>
            {submitting ? 'Sending email…' : 'Send the email'}
          </button>
          <button
            type="button"
            className="consent-form__btn consent-form__btn--ghost"
            onClick={() => navigate('/parent/consent/dob')}
            disabled={submitting}
          >
            Back
          </button>
        </div>
      </form>
    </ConsentLayout>
  );
}
